import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  GATE,
  CODE,
  REJECT,
  MEMORY,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const pop = interpolate(frame, [38, 66], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="recurring agents · what broke" color={COLORS.cyan} />
          <Big>
            It pitched the SAME idea
            <br />
            <span style={{ color: COLORS.red, opacity: 0.4 + pop * 0.6 }}>
              7 of 8 runs
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => {
  const frame = useCurrentFrame();
  const dup = interpolate(frame, [40, 90], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const rows = [
    "Adaptive Chunking, explained",
    "Why Adaptive Chunking wins",
    "Adaptive Chunking: a deep dive",
    "The case for Adaptive Chunking",
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the blank-slate problem" color={COLORS.amber} />
          <Panel accent="amber" title="inbox · same idea, fresh headline" width={840}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 24,
                lineHeight: 2.0,
                color: COLORS.text,
              }}
            >
              {rows.map((r, i) => (
                <div
                  key={i}
                  style={{
                    opacity: i === 0 ? 1 : 0.35 + dup * 0.65,
                    color: i === 0 ? COLORS.text : COLORS.amber,
                  }}
                >
                  <span style={{ color: COLORS.dim }}>run {i + 1} ▸ </span>
                  {r}
                </div>
              ))}
            </div>
          </Panel>
          <div
            style={{
              fontFamily: FONTS.body,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 26,
            }}
          >
            every run is a clean slate — so it re-discovers the same answer
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={PROBLEM} accent="amber" />
    </AbsoluteFill>
  );
};

/* 03 — INTUITION */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <Big size={74}>
          “Have I said
          <br />
          this already?”
        </Big>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 30,
            color: COLORS.dim,
            marginTop: 30,
            maxWidth: 1100,
            textAlign: "center",
          }}
        >
          compare the new output to past work — and reject before paying the model
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — GATE (pipeline) */
const PIPE: PipeNode[] = [
  { label: "mission done", value: "RUN ✓", accent: "green" },
  { label: "novelty gate", value: "SCORE", sub: "vs saved recipes", accent: "cyan" },
  { label: "decide", value: "≥ 0.3 ?", sub: "accept / reject", accent: "amber" },
  { label: "distill", value: "RECIPE", sub: "only if novel", accent: "lime" },
];
export const Scene04Gate: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.gate.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="novel-success-distillation · the gate" color={COLORS.cyan} />
      <div style={{ width: 1660 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={24} nodeWidth={220} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 20,
          color: COLORS.dim,
          marginTop: 34,
        }}
      >
        the gate runs FIRST — the expensive step never starts on a duplicate
      </div>
    </AbsoluteFill>
    <Caption lines={GATE} accent="cyan" />
  </AbsoluteFill>
);

/* 05 — CODE */
const TS = `// novel-success-distillation.ts
const NOVELTY_THRESHOLD = 0.3;   // ≥ this ⇒ "not novel"

export function jaccardSimilarity(a: string, b: string): number {
  const t = (s: string) =>
    new Set(s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
  const A = t(a), B = t(b);
  if (A.size === 0 || B.size === 0) return 0;
  let hits = 0;
  for (const w of A) if (B.has(w)) hits++;
  return hits / (A.size + B.size - hits);   // ∩ / ∪
}

for (const ex of existingSkills) {
  const sim = jaccardSimilarity(objective, ex.body);
  if (sim >= NOVELTY_THRESHOLD) return { skipped: 'not_novel' };
}`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={TS}
        language="tsx"
        filename="novel-success-distillation.ts"
        highlightLines={[2, 13, 19]}
        revealFrames={300}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — REJECT (accept vs reject + no spend) */
export const Scene06Reject: React.FC = () => {
  const frame = useCurrentFrame();
  const r = interpolate(frame, [30, 60], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const a = interpolate(frame, [70, 100], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.reject.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="check BEFORE you spend" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <div style={{ opacity: r }}>
            <Panel accent="red" title="sim ≥ 0.3 · reject" width={470}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
                <div>return <span style={{ color: COLORS.red }}>not_novel</span></div>
                <div style={{ color: COLORS.dim, marginTop: 8 }}>LLM call: <b style={{ color: COLORS.red }}>skipped</b></div>
                <div style={{ color: COLORS.dim }}>cost: <b style={{ color: COLORS.green }}>$0</b></div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: a }}>
            <Panel accent="lime" title="sim < 0.3 · accept" width={470}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
                <div>novelty <span style={{ color: COLORS.lime }}>passed</span></div>
                <div style={{ color: COLORS.dim, marginTop: 8 }}>call cheap-tier LLM</div>
                <div style={{ color: COLORS.dim }}>save reusable recipe</div>
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 30 }}>
          “no novelty → no spend” — the gate is also a cost circuit-breaker
        </div>
      </AbsoluteFill>
      <Caption lines={REJECT} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — MEMORY (mission_sent_ideas → prompt injection) */
export const Scene07Memory: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.memory.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="mission_sent_ideas · mig 171" color={COLORS.green} />
        <div style={{ display: "flex", gap: 26, alignItems: "center" }}>
          <Panel accent="green" title="already shipped (logged on send)" width={520}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, lineHeight: 1.9, color: COLORS.text }}>
              <div>• Adaptive Chunking, explained</div>
              <div>• Hybrid search done right</div>
              <div>• The 5-signal re-ranker</div>
            </div>
          </Panel>
          <div style={{ fontFamily: FONTS.display, fontSize: 52, color: COLORS.dim }}>→</div>
          <Callout label="injected into next prompt" accent="cyan" width={420}>
            <b style={{ color: COLORS.text }}>“DO NOT re-pitch these.”</b> The composer
            sees what already landed before it drafts.
          </Callout>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={MEMORY} accent="green" />
  </AbsoluteFill>
);

/* 08 — PAYOFF */
export const Scene08Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [150, 180], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [220, 250], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="dedup, in layers" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <MetricCounter value={3} startFrame={10} durationFrames={50} accent="lime" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 58, color: COLORS.text }}>
            layers that stop the repeat
          </span>
        </div>
        <div style={{ display: "flex", gap: 22, marginTop: 40 }}>
          <Callout label="layer 1 · novelty gate" accent="cyan" width={320}>
            Jaccard ≥ <b style={{ color: COLORS.text }}>0.3</b> → reject, <b style={{ color: COLORS.text }}>$0</b>
          </Callout>
          <div style={{ opacity: c1 }}>
            <Callout label="layer 2 · sent-ideas memory" accent="green" width={320}>
              <b style={{ color: COLORS.text }}>“do not re-pitch”</b> injected next run
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="layer 3 · resend guard" accent="amber" width={320}>
              stale draft? <b style={{ color: COLORS.text }}>don’t re-send</b> identical bytes
            </Callout>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: COLORS.dim, marginTop: 34 }}>
          + content-derived slug & a unique index → concurrent runs collapse to one
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          Don’t make the agent smarter.
          <br />
          <span style={{ color: COLORS.cyan }}>Give it a memory and a gate.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · self-learning skills
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
